import React, { useMemo, memo } from 'react';
import { useTab } from 'contexts/TabContext';
import { getPageComponent } from './PageComponents';
import {
  TabAreaContainer,
  TabLabel,
  TabCloseButton,
  TabContent,
  EmptyState,
  CompactTabBar,
  CompactTabPath,
  CompactTabOverlay,
  CompactTab,
} from 'styles/common.styles';

const TabPanel = ({ tab, isActive, TabComponent }) => {
  if (!TabComponent) return null;

  return (
    <div
      key={tab.id}
      style={{
        display: isActive ? 'flex' : 'none',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <TabComponent />
    </div>
  );
};

TabPanel.displayName = 'TabPanel';

const CompactTabArea = memo(() => {
  const { tabs, activeTabId, closeTab, activateTab } = useTab();

  const tabComponents = useMemo(() => {
    const components = {};
    tabs.forEach((tab) => {
      components[tab.id] = getPageComponent(tab.component, tab.componentPath);
    });
    return components;
  }, [tabs, tabs.map((t) => `${t.id}:${t.componentPath}:${t.refreshKey ?? 0}`).join('|')]);

  const activeTab = tabs.find((tab) => tab.id === activeTabId) || tabs[0];

  if (tabs.length === 0) {
    return (
      <TabAreaContainer>
        <CompactTabBar>
          <CompactTabPath />
        </CompactTabBar>
        <TabContent>
          <EmptyState>메뉴를 선택하여 탭을 열어주세요</EmptyState>
        </TabContent>
      </TabAreaContainer>
    );
  }

  return (
    <TabAreaContainer>
      <CompactTabBar>
        <CompactTabPath tabIndex={0}>{activeTab.menuPath.join(' › ')}</CompactTabPath>
        <CompactTabOverlay>
          {tabs.map((tab) => (
            <CompactTab
              key={tab.id}
              type="button"
              $active={tab.id === activeTabId}
              onClick={() => activateTab(tab.id)}
            >
              <TabLabel>{tab.label}</TabLabel>
              <TabCloseButton
                onClick={(e) => {
                  e.stopPropagation();
                  closeTab(tab.id);
                }}
              >
                ×
              </TabCloseButton>
            </CompactTab>
          ))}
        </CompactTabOverlay>
      </CompactTabBar>
      <TabContent>
        {tabs.map((tab) => {
          const TabComponent = tabComponents[tab.id];
          return (
            <TabPanel
              key={`${tab.id}-${tab.refreshKey ?? 0}`}
              tab={tab}
              isActive={tab.id === activeTabId}
              TabComponent={TabComponent}
            />
          );
        })}
      </TabContent>
    </TabAreaContainer>
  );
});

CompactTabArea.displayName = 'CompactTabArea';

export default CompactTabArea;
